import React, { useState, useEffect } from 'react';
import {
  Building2, User, Shield, Database, Save, AlertTriangle,
  Upload, Check, X, Bell, Clock, Globe,
} from 'lucide-react';
import api from '../utils/api';
import { OrgProfile, UserPreferences, PasswordForm } from '../types/modules';

const tabs = [
  { id: 'profile', label: 'Organization', icon: Building2 },
  { id: 'preferences', label: 'Preferences', icon: User },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'advanced', label: 'Advanced', icon: Database },
];

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('profile');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: string; text: string }>({ type: '', text: '' });

  // Organization Profile State
  const [orgProfile, setOrgProfile] = useState<Partial<OrgProfile>>({
    name: '',
    type: '',
    logo: '',
    tax_id: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    timezone: 'America/New_York',
  });

  // User Preferences State
  const [preferences, setPreferences] = useState<Partial<UserPreferences>>({
    notifications: {
      low_stock_alerts: true,
      batch_completion: true,
      new_orders: true,
      team_invites: true,
    },
    date_format: 'MM/DD/YYYY',
    timezone: 'America/New_York',
    theme: 'light',
  });

  // Security State
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    fetchSettings();
  }, [activeTab]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      if (activeTab === 'profile') {
        const res = await api.get('/settings/profile');
        setOrgProfile(res.data);
      } else if (activeTab === 'preferences') {
        const res = await api.get('/settings/preferences');
        setPreferences(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: string, text: string): void => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // --- Organization Profile Handlers ---
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showMessage('error', 'Logo must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      setOrgProfile({ ...orgProfile, logo: typeof result === 'string' ? result : '' });
    };
    reader.readAsDataURL(file);
  };

  const saveOrgProfile = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/profile', orgProfile);
      showMessage('success', 'Organization profile updated successfully');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // --- Preferences Handlers ---
  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/preferences', preferences);
      showMessage('success', 'Preferences updated successfully');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  // --- Security Handlers ---
  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await api.post('/settings/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      showMessage('success', 'Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // --- Advanced/Danger Zone Handlers ---
  const exportData = async () => {
    setSaving(true);
    try {
      const res = await api.post('/settings/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pourix-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      showMessage('success', 'Data exported successfully');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to export data');
    } finally {
      setSaving(false);
    }
  };

  const deleteOrganization = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will permanently delete your organization and ALL data. This action cannot be undone. Type "DELETE" to confirm.'
    );
    if (!confirmed) return;

    const doubleConfirm = window.prompt('Type DELETE to confirm:');
    if (doubleConfirm !== 'DELETE') {
      showMessage('error', 'Deletion cancelled');
      return;
    }

    setSaving(true);
    try {
      await api.delete('/settings/organization');
      showMessage('success', 'Organization deleted. Logging out...');
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your organization and preferences</p>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max sm:min-w-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#0891B2] text-[#0891B2] bg-cyan-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#0891B2] border-r-transparent"></div>
              <p className="text-gray-500 mt-2">Loading...</p>
            </div>
          ) : (
            <>
              {/* Organization Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Organization Profile</h3>

                    {/* Logo Upload */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                      <div className="flex items-center gap-4">
                        {orgProfile.logo ? (
                          <img
                            src={orgProfile.logo}
                            alt="Organization logo"
                            className="w-20 h-20 rounded-lg object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Building2 size={32} className="text-gray-400" />
                          </div>
                        )}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <div className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Upload size={16} />
                            Upload Logo
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Max 2MB. PNG, JPG, or SVG.</p>
                    </div>

                    {/* Form Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Organization Name *
                        </label>
                        <input
                          type="text"
                          value={orgProfile.name}
                          onChange={(e) => setOrgProfile({ ...orgProfile, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Industry Type
                        </label>
                        <select
                          value={orgProfile.type}
                          onChange={(e) => setOrgProfile({ ...orgProfile, type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        >
                          <option value="brewery">Brewery</option>
                          <option value="winery">Winery</option>
                          <option value="distillery">Distillery</option>
                          <option value="cidery">Cidery</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                        <input
                          type="text"
                          value={orgProfile.tax_id || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, tax_id: e.target.value })}
                          placeholder="EIN or Tax ID"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={orgProfile.phone || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={orgProfile.email || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, email: e.target.value })}
                          placeholder="contact@brewery.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                          type="text"
                          value={orgProfile.address || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, address: e.target.value })}
                          placeholder="123 Main Street"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={orgProfile.city || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          value={orgProfile.state || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, state: e.target.value })}
                          placeholder="NY"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          value={orgProfile.zip_code || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, zip_code: e.target.value })}
                          placeholder="10001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <input
                          type="text"
                          value={orgProfile.country || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, country: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                        <select
                          value={orgProfile.timezone || ''}
                          onChange={(e) => setOrgProfile({ ...orgProfile, timezone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        >
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="America/Anchorage">Alaska Time (AKT)</option>
                          <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={saveOrgProfile}
                    disabled={saving}
                    className="w-full sm:w-auto px-6 py-2.5 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Bell size={20} />
                      Notifications
                    </h3>
                    <div className="space-y-3">
                      {Object.keys(preferences.notifications).map((key) => (
                        <label key={key} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.notifications[key]}
                            onChange={(e) =>
                              setPreferences({
                                ...preferences,
                                notifications: {
                                  ...preferences.notifications,
                                  [key]: e.target.checked,
                                },
                              })
                            }
                            className="w-4 h-4 text-[#0891B2] border-gray-300 rounded focus:ring-[#0891B2]"
                          />
                          <span className="text-sm text-gray-700">
                            {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Clock size={20} />
                      Display Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date Format
                        </label>
                        <select
                          value={preferences.date_format}
                          onChange={(e) =>
                            setPreferences({ ...preferences, date_format: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        >
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                        <select
                          value={preferences.theme}
                          onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        >
                          <option value="light">Light</option>
                          <option value="dark">Dark (Coming Soon)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={savePreferences}
                    disabled={saving}
                    className="w-full sm:w-auto px-6 py-2.5 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Change Password</h3>
                    <div className="space-y-4 max-w-md">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.current_password}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, current_password: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.new_password}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, new_password: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.confirm_password}
                          onChange={(e) =>
                            setPasswordForm({ ...passwordForm, confirm_password: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={changePassword}
                    disabled={saving}
                    className="w-full sm:w-auto px-6 py-2.5 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Shield size={18} />
                    {saving ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              )}

              {/* Advanced Tab */}
              {activeTab === 'advanced' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Export Data</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Download all your organization data in JSON format.
                    </p>
                    <button
                      onClick={exportData}
                      disabled={saving}
                      className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                    >
                      <Database size={18} />
                      {saving ? 'Exporting...' : 'Export All Data'}
                    </button>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold mb-2 text-red-600 flex items-center gap-2">
                      <AlertTriangle size={20} />
                      Danger Zone
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Permanently delete this organization and all associated data. This action cannot be
                      undone.
                    </p>
                    <button
                      onClick={deleteOrganization}
                      disabled={saving}
                      className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                    >
                      <AlertTriangle size={18} />
                      {saving ? 'Deleting...' : 'Delete Organization'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
